import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { Button, DataTable, type ColumnDef } from '@shared-ui/components';

type ContainerStat = NonNullable<Awaited<ReturnType<typeof adminApi.getContainerStats>>>['containers'][number];

function formatBytes(n: number): string {
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
    if (n >= 1024) return `${(n / 1024).toFixed(2)} KB`;
    return `${n} B`;
}

function formatCount(n: number): string {
    return n.toLocaleString();
}

export function ContainerStats() {
    const { data, isLoading, isFetching, refetch, dataUpdatedAt, error } = useQuery({
        queryKey: ['container-stats'],
        queryFn: () => adminApi.getContainerStats(),
        staleTime: 5 * 60_000,
    });

    const columns = useMemo<ColumnDef<ContainerStat>[]>(() => [
        { key: 'name', header: 'Container', render: (r) => <span className="aj-mono">{r.name}</span> },
        { key: 'docCount', header: 'Total docs', className: 'aj-num', render: (r) => formatCount(r.docCount) },
        { key: 'sampled', header: 'Sampled', className: 'aj-num', render: (r) => r.sampled },
        { key: 'min', header: 'Min', className: 'aj-num', render: (r) => r.sampled === 0 ? '—' : formatBytes(r.sizeBytes.min) },
        { key: 'median', header: 'Median', className: 'aj-num', render: (r) => r.sampled === 0 ? '—' : formatBytes(r.sizeBytes.median) },
        { key: 'avg', header: 'Avg', className: 'aj-num', render: (r) => r.sampled === 0 ? '—' : formatBytes(r.sizeBytes.avg) },
        { key: 'max', header: 'Max', className: 'aj-num', render: (r) => r.sampled === 0 ? '—' : formatBytes(r.sizeBytes.max) },
        {
            key: 'newest',
            header: 'Newest',
            render: (r) => r.newest ? format(new Date(r.newest.ts), 'MMM d HH:mm') : '—',
        },
        {
            key: 'error',
            header: '',
            render: (r) => r.error ? <span className="aj-error-cell" title={r.error}>err</span> : '',
        },
    ], []);

    return (
        <div className="settings-section">
            <div className="section-header aj-section-header">
                <div>
                    <h2>Container Stats</h2>
                    <p className="section-description">
                        Per-container document count and size distribution over the last 100
                        docs by <code>_ts DESC</code> (created/updated). Use this to spot
                        containers whose per-doc size is growing. Sorted by average size.
                    </p>
                </div>
                <Button onClick={() => refetch()} disabled={isFetching}>
                    {isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
            </div>

            {error ? (
                <div className="aj-error-cell">
                    Failed to load: {error instanceof Error ? error.message : String(error)}
                </div>
            ) : null}

            {data ? (
                <p className="section-description" style={{ marginTop: 0 }}>
                    Generated {format(new Date(data.generatedAt), 'MMM d HH:mm:ss')}
                    {dataUpdatedAt ? ` · refreshed ${format(new Date(dataUpdatedAt), 'HH:mm:ss')}` : ''}
                </p>
            ) : null}

            <DataTable
                data={data?.containers ?? []}
                columns={columns}
                rowKey={(r) => r.name}
                loading={isLoading}
                emptyMessage="No containers found"
            />
        </div>
    );
}
